// =============================================================================
// TwoFactorSetup.js  (A4 – Zwei-Faktor-Authentifizierung einrichten)
// -----------------------------------------------------------------------------
// Fuehrt durch die Einrichtung: Secret erzeugen -> QR-Code scannen oder Secret
// manuell eintragen -> Code bestaetigen -> einmalige Backup-Codes sichern.
// Fuer Admins (mit Schonfrist) ist die Einrichtung verpflichtend, fuer MainVO
// und SubVO freiwillig.
// =============================================================================

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import UserService from "../services/user.service";

const TwoFactorSetup = () => {
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // { secret, otpauthUrl }
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    UserService.get2faStatus()
      .then((res) => setStatus(res.data))
      .catch(() => setStatus(null));
  }, []);

  const startSetup = () => {
    setError("");
    setLoading(true);
    UserService.setup2fa()
      .then((res) => setSetupData(res.data))
      .catch((e) => setError((e.response && e.response.data && e.response.data.message) || "Einrichtung fehlgeschlagen."))
      .finally(() => setLoading(false));
  };

  const confirm = (event) => {
    event.preventDefault();
    const clean = code.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(clean)) {
      setError("Bitte einen 6-stelligen Code eingeben.");
      return;
    }
    setError("");
    setLoading(true);
    UserService.confirm2fa(clean)
      .then((res) => setBackupCodes(res.data.backup_codes || []))
      .catch((e) => setError((e.response && e.response.data && e.response.data.message) || "Der Code ist ungueltig."))
      .finally(() => setLoading(false));
  };

  // Abschluss nach dem Sichern der Backup-Codes.
  if (backupCodes) {
    return (
      <div className="myn-auth">
        <div className="myn-card myn-auth-card">
          <h1>Backup-Codes sichern</h1>
          <p>
            Bewahren Sie diese Einmal-Codes an einem sicheren Ort auf. Jeder Code
            funktioniert genau einmal, falls Sie keinen Zugriff auf Ihre
            Authenticator-App haben.
          </p>
          <ul className="myn-backup-codes" style={{ fontFamily: "monospace", fontSize: "1.1rem", lineHeight: 1.8 }}>
            {backupCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <button
            className="myn-btn myn-btn--primary myn-btn--block"
            onClick={() => {
              navigate("/customerinfo");
              window.location.reload();
            }}
          >
            Ich habe die Codes gesichert – weiter
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="myn-auth">
      <div className="myn-card myn-auth-card">
        <h1>Zwei-Faktor-Authentifizierung einrichten</h1>
        {status && status.mandatory && (
          <div className="myn-alert myn-alert--info" role="alert">
            Für Administratoren ist die Zwei-Faktor-Authentifizierung
            verpflichtend. Bitte richten Sie sie jetzt ein.
          </div>
        )}

        {!setupData ? (
          <>
            <p>
              Schützen Sie Ihr Konto zusätzlich mit einem zeitbasierten
              Einmalcode aus einer Authenticator-App (z. B. Google Authenticator,
              Microsoft Authenticator, Aegis).
            </p>
            <button
              className="myn-btn myn-btn--primary myn-btn--block"
              onClick={startSetup}
              disabled={loading}
            >
              {loading && <span className="spinner-border spinner-border-sm" />}
              <span>Einrichtung starten</span>
            </button>
          </>
        ) : (
          <>
            <p>1. Scannen Sie diesen QR-Code mit Ihrer Authenticator-App:</p>
            <div style={{ textAlign: "center", margin: "1rem 0" }}>
              <QRCodeSVG value={setupData.otpauthUrl} size={180} includeMargin />
            </div>
            <p>
              Oder tragen Sie den Schlüssel manuell ein:
              <br />
              <code style={{ wordBreak: "break-all" }}>{setupData.secret}</code>
            </p>
            <form onSubmit={confirm}>
              <div className="myn-field">
                <label className="myn-label" htmlFor="confirmCode">
                  2. Geben Sie den angezeigten 6-stelligen Code ein:
                </label>
                <input
                  id="confirmCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="myn-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-stelliger Code"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="myn-btn myn-btn--primary myn-btn--block"
                disabled={loading}
              >
                {loading && <span className="spinner-border spinner-border-sm" />}
                <span>Aktivieren</span>
              </button>
            </form>
          </>
        )}

        {error && (
          <div className="myn-alert myn-alert--danger" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSetup;
